import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { IQueryResult, IQueryService, Key, QueryResult, SearchExpression, SortExpression } from '@kpd/framework-ngx';
import { IPermissionDTO, IRoleDTO } from './role.service';
import { apiUrl } from 'environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@kpd/framework-ngx';
import { map } from 'rxjs/operators';
import { Permission } from 'app/shared/models/permission';
import { ApplicationService } from './application.service';
import { flattenArrays } from '../utils/array';
import { IPermissionSetDTO } from './permissionset.service';
import { Application } from '../models/application';
import { Permissionset } from '../models/permissionset';
import { Role } from '../models/role';
import { IUserDTO } from './user.service';
import { User } from '../models/user';

@Injectable()
export class PermissionService implements IQueryService {
  allTenantPermissions: Permission[] = [];
  allTenantApplications: Application[] = [];

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private applicationService: ApplicationService,
  ) {}

  get(
    searchExpression?: SearchExpression | undefined,
    sortExpression?: SortExpression | undefined,
    pageNumber?: number | undefined,
    pageSize?: number | undefined,
  ): Observable<IQueryResult> {
    return this.loadTenantPermissions(true).pipe(
      map((items) => {
        if (searchExpression && searchExpression.criterias.length > 0) {
          items = items.filter((item) => searchExpression.checkItem(item));
        }
        if (sortExpression) {
          items = sortExpression.sortItems(items);
        }
        if (pageSize && pageNumber && pageSize < items.length) {
          items = items.slice(100 * pageNumber - 100, 100 * pageNumber);
        }

        return new QueryResult(items, items.length);
      }),
    );
  }

  loadTenantPermissions(refresh = false): Observable<Permission[]> {
    if (this.allTenantPermissions.length > 0 && !refresh) {
      return of(this.allTenantPermissions);
    }
    return this.loadTenantApplications().pipe(
      switchMap((applications) => {
        const obs$: Observable<IQueryResult>[] = [];
        applications.forEach((app) => {
          obs$.push(this.getPermissionsForApplication(app.name));
        });
        return forkJoin(obs$);
      }),
      map((data) => {
        const permissionsArrays: Permission[][] = data.map((d) => d.result);
        let items = flattenArrays(permissionsArrays);
        this.allTenantPermissions = items;
        return items;
      }),
    );
  }

  getPermissionSets(permissionId: string): Observable<Permissionset[]> {
    return this.httpClient
      .get<IPermissionSetDTO[]>(`${apiUrl()}/tenants/${this.tenant.id}/permissions/${permissionId}/permissionsets`)
      .pipe(
        map((dtos: IPermissionSetDTO[]) => {
          return dtos.map((dto) => {
            const item = new Permissionset(dto);
            return item;
          });
        }),
      );
  }

  getRoles(permissionId: string): Observable<Role[]> {
    return this.httpClient
      .get<IRoleDTO[]>(`${apiUrl()}/tenants/${this.tenant.id}/permission/${permissionId}/roles`)
      .pipe(
        map((dtos: IRoleDTO[]) => {
          return dtos.map((dto) => {
            const item = new Role(dto);
            return item;
          });
        }),
      );
  }

  getUsers(permissionId: string): Observable<User[]> {
    return this.httpClient
      .get<IUserDTO[]>(`${apiUrl()}/tenants/${this.tenant.id}/permissions/${permissionId}/users`)
      .pipe(
        map((dtos: IUserDTO[]) => {
          return dtos.map((dto) => {
            const item = new User(dto);
            return item;
          });
        }),
      );
  }

  loadTenantApplications(refresh = false): Observable<Application[]> {
    if (this.allTenantApplications.length > 0 && !refresh) {
      return of(this.allTenantApplications);
    }
    return this.applicationService.getApplicationsByTenant().pipe(tap((apps) => (this.allTenantApplications = apps)));
  }

  getById(key: Key<string>, _clearCache?: boolean | undefined): Observable<Permission> {
    if (this.allTenantPermissions.length > 0) {
      return of(this.allTenantPermissions.find((p) => p.id === key.id) as Permission);
    }
    return this.loadTenantPermissions().pipe(
      map((items) => {
        return items.find((p) => p.id === key.id) as Permission;
      }),
    );
  }

  get tenant(): Key<string> {
    return this.authService.getTenant().key;
  }

  getPermissionsForApplication(application: string): Observable<IQueryResult> {
    return this.httpClient
      .get<IPermissionDTO[]>(`${apiUrl()}/applications/${encodeURIComponent(application)}/permissions`)
      .pipe(
        map((dtos: IPermissionDTO[]) => {
          return {
            result: dtos.map((dto) => {
              const item = new Permission(dto);
              item.application = application;
              return item;
            }),
            totalCount: dtos.length,
          };
        }),
      );
  }
}
